// Web walker integration. Thin wrapper around elka-ts (the
// authoritative implementation that's gated by the cross-platform
// corpus). Builds an EvalContext from a chores Todo + host frame.
//
// The chores Schema (chores.todo_eval_context, version 1) is
// loaded once at module init from the codegen'd JSON sibling.

import {
  Schema,
  Walker,
  mapContext,
  boolValue,
  int64Value,
  doubleValue,
  stringValue,
  timestampSeconds,
  arrayValue,
  fromLiteral,
  type EvalContext,
  type Expr,
  type FormattedString,
  type Value,
} from '@elka/runtime';

import type { Todo } from '../../infra/types/domain';
import { AppDefaults } from '../../infra/wasm/AppDefaults';

// schema.gen.json is a checked-in mirror of
// internal/domain/predicate/schema.json. CRA blocks imports outside
// `web/src/` for the prod build, so the Go codegen side syncs the
// file via `make web-schema-sync` (Makefile target). The iOS bundle
// has the same sibling at ios/Sources/Core/Predicate/predicate-schema.json.
import schemaJSON from './schema.gen.json';

// The JSON's inferred type is narrower than SchemaProto (which has
// optional fields and string-typed enums). Cast through unknown.
export const choresSchema = new Schema(schemaJSON as unknown as ConstructorParameters<typeof Schema>[0]);
export const walker = new Walker(choresSchema);

// HostFrame is the bag of "outside-the-todo" eval-context fields:
// now, today_start, tomorrow_start, viewing user, household frame.
// Resolved per-evaluation, not per-todo, so callers compute it
// once and pass it through.
export interface HostFrame {
  nowSec: bigint;
  todayStartSec: bigint;
  tomorrowStartSec: bigint;
  // dayEndSec is the end of the user's logical day (today_start +
  // day_start_hour h, OR today_start + day_start_hour h + 24h
  // depending on whether `now` is past the user's day start).
  // Read by filter.today / filter.upcoming / filter.completed_today.
  dayEndSec: bigint;
  // thisWeekEndSec / nextWeekEndSec drive the Upcoming sub-buckets.
  // Sunday-start week (next Sunday 00:00 in user.tz), bumped to at
  // least dayAfterTomorrow so Friday/Saturday don't produce a
  // 0–1 day "This week" sliver. Read by filter.upcoming.tomorrow
  // / .this_week / .next_week / .later.
  thisWeekEndSec: bigint;
  nextWeekEndSec: bigint;
  userID: string;
  userTZ: string;
  // IETF BCP 47 locale tag (e.g. "en-US", "ru-RU"). Plumbed for
  // renderer use (Intl.* locale pinning); rule bodies may
  // reference `user.locale` directly. Display spec §1, Phase 1.
  userLocale: string;
  defaultHour: bigint;
  // [0,8] inclusive — wall-clock hour that defines the user's
  // logical day start. Zero means "midnight = day start" (calendar
  // day); 6 is the historical hardcoded boundary.
  dayStartHour: bigint;
  householdID: string;
  memberIDs: string[];
}

export function defaultHostFrame(
  userID: string,
  userTZ: string,
  memberIDs: string[],
  householdID = '',
  defaultHour = AppDefaults.defaultHourFallback,
  dayStartHour = AppDefaults.dayStartHourFallback,
  userLocale = '',
): HostFrame {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  const todaysStart = new Date(todayStart.getTime() + dayStartHour * 3600000);
  const dayEnd = now < todaysStart
    ? todaysStart
    : new Date(todaysStart.getTime() + 24 * 60 * 60 * 1000);
  // Sunday-start week. getDay returns 0=Sun..6=Sat — so days
  // until next Sunday is (7 - weekday) for Mon–Sat and 7 when
  // today IS Sunday (we want next Sunday, not today).
  const weekday = todayStart.getDay();
  const daysUntilNextSunday = weekday === 0 ? 7 : 7 - weekday;
  const oneDay = 24 * 60 * 60 * 1000;
  let thisWeekEnd = new Date(todayStart.getTime() + daysUntilNextSunday * oneDay);
  const dayAfterTomorrow = new Date(todayStart.getTime() + 2 * oneDay);
  if (dayAfterTomorrow.getTime() > thisWeekEnd.getTime()) {
    thisWeekEnd = dayAfterTomorrow;
  }
  const nextWeekEnd = new Date(thisWeekEnd.getTime() + 7 * oneDay);
  // Empty userLocale falls back to the browser's negotiated
  // language. The renderer pins en_US_POSIX as default when its
  // own ctx.locale is empty — host-frame value is just what the
  // host has.
  const resolvedLocale = userLocale ||
    (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
  return {
    nowSec: BigInt(Math.floor(now.getTime() / 1000)),
    todayStartSec: BigInt(Math.floor(todayStart.getTime() / 1000)),
    tomorrowStartSec: BigInt(Math.floor(tomorrowStart.getTime() / 1000)),
    dayEndSec: BigInt(Math.floor(dayEnd.getTime() / 1000)),
    thisWeekEndSec: BigInt(Math.floor(thisWeekEnd.getTime() / 1000)),
    nextWeekEndSec: BigInt(Math.floor(nextWeekEnd.getTime() / 1000)),
    userID,
    userTZ,
    userLocale: resolvedLocale,
    defaultHour: BigInt(defaultHour),
    dayStartHour: BigInt(dayStartHour),
    householdID,
    memberIDs,
  };
}

// buildTodoContextValues mirrors internal/domain/predicate/drift.go's
// BuildTodoContext on the Go side, returning the raw path→Value map.
// Kept separate from buildTodoContext (which wraps it in a mapContext
// closure) so overlay variants — buildProjectTodoContext — can rebuild a
// fresh context with one key overridden without trying to read values
// back out of the opaque closure mapContext returns.
function buildTodoContextValues(t: Todo, host: HostFrame): Record<string, Value> {
  const values: Record<string, Value> = {
    'todo.id': stringValue(t.id),
    'todo.title': stringValue(t.title ?? ''),
    'todo.status': stringValue(t.status ?? ''),
    'todo.owner_id': stringValue(t.owner_id ?? ''),
    'todo.is_private': boolValue(Boolean((t as Todo & { is_private?: boolean }).is_private)),
    'todo.recurrence_rule': stringValue(t.recurrence_rule ?? ''),
    'todo.vanish_at_time': stringValue((t as Todo & { vanish_at_time?: string }).vanish_at_time ?? ''),
    'todo.vanishing_enabled': boolValue(Boolean((t as Todo & { vanishing_enabled?: boolean }).vanishing_enabled)),
    'todo.notify_enabled': boolValue(Boolean((t as Todo & { notify_enabled?: boolean }).notify_enabled)),
    'todo.notify_before_ms': int64Value(BigInt((t as Todo & { notify_before?: number }).notify_before ?? 0)),
    'todo.priority': stringValue((t as Todo & { priority?: string }).priority ?? ''),
    'todo.type_tag': stringValue((t as Todo & { type_tag?: string }).type_tag ?? ''),
    'todo.source_plugin': stringValue((t as Todo & { source_plugin?: string }).source_plugin ?? ''),
    'todo.visibility': stringValue((t as Todo & { visibility?: string }).visibility ?? ''),
    'todo.close_reason': stringValue(t.close_reason ?? ''),
    // todo.has_open_children is a tree-relational fact (schema 12) the
    // generic single-todo context can't know. Defaults false here, exactly
    // like the Go drift.go side; buildProjectTodoContext overrides it with
    // the real per-row value when classifying an outline.
    'todo.has_open_children': boolValue(false),
    'user.id': stringValue(host.userID),
    'user.tz': stringValue(host.userTZ),
    'user.locale': stringValue(host.userLocale),
    'user.preferences.default_hour': int64Value(host.defaultHour),
    'user.preferences.day_start_hour': int64Value(host.dayStartHour),
    'household.id': stringValue(host.householdID),
    now: timestampSeconds(host.nowSec),
    user_tz: stringValue(host.userTZ),
    today_start: timestampSeconds(host.todayStartSec),
    tomorrow_start: timestampSeconds(host.tomorrowStartSec),
    day_end: timestampSeconds(host.dayEndSec),
    this_week_end: timestampSeconds(host.thisWeekEndSec),
    next_week_end: timestampSeconds(host.nextWeekEndSec),
    // `bucket` defaults to epoch-0; section-header surfaces
    // override it via buildSectionHeaderContext.
    bucket: timestampSeconds(0n),
    'event.start_time': timestampSeconds(0n),
    'event.end_time': timestampSeconds(0n),
    'event.all_day': boolValue(false),
    'event.same_day': boolValue(false),
    'household.member_ids': arrayValue(
      'AST_TYPE_STRING',
      host.memberIDs.map(stringValue),
    ),
  };
  if (t.due_at) {
    values['todo.due_at'] = timestampSeconds(BigInt(Math.floor(new Date(t.due_at).getTime() / 1000)));
  } else {
    values['todo.due_at'] = { type: 'AST_TYPE_NULL' };
  }
  const tWithOrig = t as Todo & { original_due_at?: string | null };
  if (tWithOrig.original_due_at) {
    values['todo.original_due_at'] = timestampSeconds(
      BigInt(Math.floor(new Date(tWithOrig.original_due_at).getTime() / 1000)),
    );
  } else {
    values['todo.original_due_at'] = { type: 'AST_TYPE_NULL' };
  }
  if ((t as Todo & { completed_at?: string }).completed_at) {
    const ms = new Date((t as Todo & { completed_at: string }).completed_at).getTime();
    values['todo.completed_at'] = timestampSeconds(BigInt(Math.floor(ms / 1000)));
  } else {
    values['todo.completed_at'] = { type: 'AST_TYPE_NULL' };
  }
  // todo.created_at is non-null on the schema; absent input means
  // "0 seconds since epoch" rather than null. ShouldVanish-style
  // rules anchor against created_at, so a missing created_at
  // would be treated as the dawn of time and effectively skip
  // the boundary check — wrong, but locally consistent.
  const createdAt = (t as Todo & { created_at?: string }).created_at;
  values['todo.created_at'] = createdAt
    ? timestampSeconds(BigInt(Math.floor(new Date(createdAt).getTime() / 1000)))
    : timestampSeconds(0n);
  const updatedAt = (t as Todo & { updated_at?: string }).updated_at;
  values['todo.updated_at'] = updatedAt
    ? timestampSeconds(BigInt(Math.floor(new Date(updatedAt).getTime() / 1000)))
    : timestampSeconds(0n);
  // todo.sort_order is the manual-reorder position (nullable). Exposed so the
  // shared rule.order.todo.inbox evaluates here exactly as on the Go/iOS side
  // (asc, nulls last) instead of trapping UNKNOWN_VAR_PATH and falling back.
  const sortOrder = (t as Todo & { sort_order?: number | null }).sort_order;
  values['todo.sort_order'] =
    sortOrder != null ? doubleValue(sortOrder) : { type: 'AST_TYPE_NULL' };
  // todo.notify_at is computed (rule.default.notify_at) — not
  // stored on the Todo. Mirror the Go side: input context
  // exposes Null so a stray Var reference doesn't throw
  // UNKNOWN_VAR_PATH.
  values['todo.notify_at'] = { type: 'AST_TYPE_NULL' };
  return values;
}

// buildTodoContext mirrors internal/domain/predicate/drift.go's
// BuildTodoContext on the Go side. Field-by-field correspondence.
export function buildTodoContext(t: Todo, host: HostFrame): EvalContext {
  return mapContext(buildTodoContextValues(t, host));
}

// buildProjectTodoContext builds the same per-row context as
// buildTodoContext but supplies the real `todo.has_open_children`
// tree-relational fact (schema 12) instead of the default false. Only
// the project-outline row-state classifier (classify.todo.*) reads this
// fact — every other surface uses buildTodoContext where the default
// false is correct (no tree knowledge). The host computes
// hasOpenChildren from the project's children map before calling.
export function buildProjectTodoContext(
  t: Todo,
  host: HostFrame,
  hasOpenChildren: boolean,
): EvalContext {
  const values = buildTodoContextValues(t, host);
  values['todo.has_open_children'] = boolValue(hasOpenChildren);
  return mapContext(values);
}

// buildEventBlockSubtitleContext builds an eval context with the
// `event.*` host-frame fields overlaid for the
// `display.event.block.subtitle` surface (item 7b). The Todo
// argument is ignored by the event rule body; pass a minimal stub.
export function buildEventBlockSubtitleContext(
  t: Todo,
  host: HostFrame,
  eventStartSec: bigint,
  eventAllDay: boolean,
): EvalContext {
  const values = buildTodoContextValues(t, host);
  values['event.start_time'] = timestampSeconds(eventStartSec);
  values['event.all_day'] = boolValue(eventAllDay);
  return mapContext(values);
}

// buildEventDetailSubtitleContext overlays the full event range for
// `display.event.detail.subtitle`. The host pre-computes
// `event.same_day` (start + end fall on the same calendar day in
// the host tz) so the rule body doesn't need three-component date
// arithmetic.
export function buildEventDetailSubtitleContext(
  t: Todo,
  host: HostFrame,
  eventStartSec: bigint,
  eventEndSec: bigint,
  eventAllDay: boolean,
  eventSameDay: boolean,
): EvalContext {
  const values = buildTodoContextValues(t, host);
  values['event.start_time'] = timestampSeconds(eventStartSec);
  values['event.end_time'] = timestampSeconds(eventEndSec);
  values['event.all_day'] = boolValue(eventAllDay);
  values['event.same_day'] = boolValue(eventSameDay);
  return mapContext(values);
}

// buildSectionHeaderContext builds an eval context for surfaces like
// `display.todo.log.section_header` that need a per-call `bucket`
// override. The Todo argument is ignored by the section-header rule;
// pass a minimal stub.
export function buildSectionHeaderContext(
  t: Todo,
  host: HostFrame,
  bucketSec: bigint,
): EvalContext {
  const values = buildTodoContextValues(t, host);
  values.bucket = timestampSeconds(bucketSec);
  return mapContext(values);
}

// EvalFilter walks a FilterRule's predicate against the given
// context. Returns false on any walker error (caller treats that
// as "fall through to imperative path").
export function evalFilter(predicate: Expr, ctx: EvalContext): boolean {
  try {
    const v = walker.eval(predicate, ctx);
    return v.type === 'AST_TYPE_BOOL' && v.value;
  } catch (e) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[predicate] walker error', e);
    }
    return false;
  }
}

// evalDisplay walks a DisplayRule's body Expr and returns the
// resulting FormattedString. Returns null on (a) walker error,
// (b) body returning something other than Array<Segment>.
//
// Hosts MUST forward the captured error to QPLErrorReporter when
// they care about telemetry. Per spec §3.5 the surface renders
// empty post-cutover; the legacy imperative path provides the
// fallback while the kill switch is on.
//
// Spec: chores docs/plans/elka-display-spec.md §3.2 + §3.5.
export function evalDisplay(body: Expr, ctx: EvalContext): FormattedString | null {
  try {
    return evalDisplayThrowing(body, ctx);
  } catch (e) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[predicate] display walker error', e);
    }
    return null;
  }
}

export function evalDisplayThrowing(body: Expr, ctx: EvalContext): FormattedString {
  const v = walker.eval(body, ctx);
  if (v.type !== 'AST_TYPE_ARRAY' || v.elementType !== 'AST_TYPE_SEGMENT') {
    throw new Error(
      `display body produced ${v.type}, expected Array<Segment>`,
    );
  }
  const segments = v.items
    .filter(
      (item): item is Extract<typeof item, { type: 'AST_TYPE_SEGMENT' }> =>
        item.type === 'AST_TYPE_SEGMENT',
    )
    .map((item) => item.segment);
  return { segments };
}

// fromLiteral re-exported for callers that need the runtime value
// builder (e.g., tests).
export { fromLiteral };

// evalOrder walks an OrderRule's primary + tiebreaker Exprs over a
// single context. Returns null on any walker trap so the caller can
// fall back to the imperative comparator.
export function evalOrder(
  primary: Expr,
  tiebreaker: Expr | undefined,
  ctx: EvalContext,
): { primary: Value; tiebreaker?: Value } | null {
  try {
    const p = walker.eval(primary, ctx);
    if (tiebreaker) {
      const t = walker.eval(tiebreaker, ctx);
      return { primary: p, tiebreaker: t };
    }
    return { primary: p };
  } catch (e) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[predicate] order walker error', e);
    }
    return null;
  }
}

// compareElkaValues returns -1/0/1 ordering for two elka Values.
// Same-type compares: numeric for Int64/Double/Timestamp/Duration,
// localeCompare for String. Null sorts last (matches the iOS
// compareElkaValues counterpart). Mixed types fall back to 0 so the
// caller's tiebreaker decides.
export function compareElkaValues(a: Value, b: Value): -1 | 0 | 1 {
  if (a.type === 'AST_TYPE_NULL' && b.type === 'AST_TYPE_NULL') return 0;
  if (a.type === 'AST_TYPE_NULL') return 1;
  if (b.type === 'AST_TYPE_NULL') return -1;
  if (a.type !== b.type) return 0;
  switch (a.type) {
    case 'AST_TYPE_INT64': {
      const av = a.value as bigint;
      const bv = (b as { value: bigint }).value;
      return av < bv ? -1 : av > bv ? 1 : 0;
    }
    case 'AST_TYPE_DOUBLE': {
      const av = a.value;
      const bv = (b as { value: number }).value;
      return av < bv ? -1 : av > bv ? 1 : 0;
    }
    case 'AST_TYPE_TIMESTAMP':
    case 'AST_TYPE_DURATION': {
      const av = a.seconds;
      const bv = (b as { seconds: bigint }).seconds;
      return av < bv ? -1 : av > bv ? 1 : 0;
    }
    case 'AST_TYPE_STRING': {
      const r = a.value.localeCompare((b as { value: string }).value);
      return r < 0 ? -1 : r > 0 ? 1 : 0;
    }
    default:
      return 0;
  }
}
